import { useContext, useEffect, useState } from "react";
import { LeagueContext, League } from "../context/LeagueContext";
import { apiGet, ApiError } from "../utils/apiClient";
import { API_BASE_URL } from "../config/api";

/**
 * Domain resolution state
 */
export type DomainResolutionState =
  | "loading"
  | "main_domain"
  | "custom_domain_resolved"
  | "custom_domain_not_found"
  | "error";

export interface DomainResolutionResult {
  state: DomainResolutionState;
  league: League | null;
  error: string | null;
}

/**
 * useLeagueFromDomain
 *
 * Detects if the app is running on a custom domain and resolves the league.
 *
 * When a league registers a custom domain (e.g. northsidewolves.com) and
 * points it at the Podium server, this hook will:
 *   1. Read `window.location.hostname`
 *   2. Check if it's the main Podium domain or a custom domain
 *   3. If custom: call /domain_lookup?hostname=<hostname> to resolve the league
 *   4. Set that league as the current league in LeagueContext
 *
 * This hook should be called once high in the component tree (e.g. in
 * App.tsx or a root layout component) before any route-level rendering.
 *
 * Returns the resolution state to allow conditional routing.
 */
export function useLeagueFromDomain(): DomainResolutionResult {
  const { setCurrentLeague } = useContext(LeagueContext);
  const [result, setResult] = useState<DomainResolutionResult>({
    state: "loading",
    league: null,
    error: null,
  });

  useEffect(() => {
    const hostname = window.location.hostname;
    const mainDomain = process.env.REACT_APP_MAIN_DOMAIN || "localhost";

    console.log("[useLeagueFromDomain] Starting domain resolution", {
      hostname,
      mainDomain,
      REACT_APP_MAIN_DOMAIN: process.env.REACT_APP_MAIN_DOMAIN,
      API_BASE_URL,
    });

    // Check if this is the main Podium domain
    const isMainDomain =
      hostname === mainDomain ||
      hostname.endsWith(`.${mainDomain}`) ||
      hostname === "localhost" ||
      hostname === "127.0.0.1";

    console.log("[useLeagueFromDomain] Domain check result", {
      isMainDomain,
      checks: {
        exactMatch: hostname === mainDomain,
        subdomain: hostname.endsWith(`.${mainDomain}`),
        isLocalhost: hostname === "localhost",
        is127: hostname === "127.0.0.1",
      },
    });

    if (isMainDomain) {
      console.log(
        "[useLeagueFromDomain] Main domain detected - no lookup needed",
      );
      setResult({
        state: "main_domain",
        league: null,
        error: null,
      });
      return;
    }

    // Custom domain detected - resolve league
    const lookupUrl = `${API_BASE_URL}/domain_lookup?hostname=${encodeURIComponent(
      hostname,
    )}`;
    console.log("[useLeagueFromDomain] Custom domain detected, calling API", {
      lookupUrl,
    });

    apiGet<League>(lookupUrl, {
      skipLeagueHeader: true,
    })
      .then((league) => {
        console.log("[useLeagueFromDomain] Domain resolved successfully", {
          league,
        });
        setCurrentLeague(league);
        setResult({
          state: "custom_domain_resolved",
          league,
          error: null,
        });
      })
      .catch((error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        const isNotFound = error instanceof ApiError && error.status === 404;

        console.error("[useLeagueFromDomain] Domain lookup failed", {
          error,
          errorMessage,
          status: error instanceof ApiError ? error.status : undefined,
          isNotFound,
        });

        setResult({
          state: isNotFound ? "custom_domain_not_found" : "error",
          league: null,
          error: errorMessage,
        });
      });
  }, [setCurrentLeague]);

  return result;
}
